import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import BackspaceIcon from '@mui/icons-material/Backspace';

export default function PinPromptDialog({
  open,
  onClose,
  onSuccess,
  expectedPin,
  title = 'Security PIN Required',
  description = 'Enter your security PIN to access this section.',
}) {
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setPin('');
      setError('');
      setShowPin(false);
    }
  }, [open]);

  const handleVerify = () => {
    if (!pin) {
      setError('Please enter your PIN');
      return;
    }
    const cleanEntered = String(pin).trim();
    const cleanExpected = String(expectedPin || '').trim();

    if (cleanEntered === cleanExpected) {
      setError('');
      setPin('');
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } else {
      setError('Incorrect PIN. Please try again.');
      setPin('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleVerify();
    }
  };

  const handleNumpad = (digit) => {
    if (pin.length < 10) {
      setPin((prev) => prev + digit);
      setError('');
    }
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
    setError('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          textAlign: 'center',
        },
      }}
    >
      <DialogTitle sx={{ pt: 2, pb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            bgcolor: error ? 'error.lighter' : 'primary.lighter',
            color: error ? 'error.main' : 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
            border: '2px solid',
            borderColor: error ? 'error.light' : 'primary.light',
          }}
        >
          {error ? <LockIcon fontSize="large" /> : <LockOpenIcon fontSize="large" />}
        </Box>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, px: 1 }}>
          {description}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: '0.85rem' }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          type={showPin ? 'text' : 'password'}
          placeholder="Enter PIN"
          value={pin}
          onChange={(e) => {
            setPin(e.target.value);
            setError('');
          }}
          onKeyDown={handleKeyDown}
          inputProps={{
            maxLength: 10,
            style: {
              textAlign: 'center',
              fontSize: '1.4rem',
              letterSpacing: showPin ? '4px' : '8px',
              fontWeight: 700,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPin(!showPin)}
                  edge="end"
                  size="small"
                  title={showPin ? 'Hide PIN' : 'Show PIN'}
                >
                  {showPin ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Optional Numpad for Touch / Fast input */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            mb: 1,
          }}
        >
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
            <Button
              key={digit}
              variant="outlined"
              onClick={() => handleNumpad(digit)}
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                py: 1,
                borderRadius: 2,
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {digit}
            </Button>
          ))}
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setPin('')}
            sx={{ fontSize: '0.8rem', fontWeight: 600, borderRadius: 2 }}
          >
            Clear
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleNumpad('0')}
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              py: 1,
              borderRadius: 2,
              borderColor: 'divider',
              color: 'text.primary',
            }}
          >
            0
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleBackspace}
            sx={{ borderRadius: 2 }}
          >
            <BackspaceIcon fontSize="small" />
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="inherit" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleVerify}
          disabled={!pin}
          sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
        >
          Unlock
        </Button>
      </DialogActions>
    </Dialog>
  );
}
